"use client";

import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export type CountryOption = {
  id: number;
  name: string;
};

type CountryComboboxProps = {
  countries: CountryOption[];
  value: string;
  onValueChange: (value: string) => void;
  id?: string;
  disabled?: boolean;
  "aria-invalid"?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
};

export function CountryCombobox({
  countries,
  value,
  onValueChange,
  id,
  disabled,
  "aria-invalid": ariaInvalid,
  placeholder = "Select a country…",
  searchPlaceholder = "Search country…",
}: CountryComboboxProps) {
  const [open, setOpen] = React.useState(false);

  const selected = value
    ? countries.find((c) => String(c.id) === value)
    : undefined;
  const summary = selected ? selected.name : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          disabled={disabled}
          className="h-auto min-h-9 w-full justify-between py-2 font-normal"
        >
          <span className="min-w-0 flex-1 truncate text-left">{summary}</span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-2rem)] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {countries.map((c) => {
                const idStr = String(c.id);
                return (
                  <CommandItem
                    key={c.id}
                    value={`${idStr} ${c.name}`}
                    keywords={[c.name, idStr, c.name.toLowerCase()]}
                    onSelect={() => {
                      onValueChange(idStr);
                      setOpen(false);
                    }}
                  >
                    <span className="min-w-0 flex-1 truncate">{c.name}</span>
                    <Check
                      className={cn(
                        "ml-auto size-4 shrink-0",
                        value === idStr ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
